/* Public payslip signature page — iter56.
 * Path: /sign-payslip/:token
 * Anyone with the token can view + sign the payslip. No auth.
 *
 * Two signature modes at the employee's choice:
 *   1. Canvas drawing (finger/mouse) — captured as PNG data-URL and stamped
 *      onto the PDF signature block.
 *   2. Click "J'accepte et signe" — records timestamp + IP; PDF gets an
 *      "SIGNÉ ÉLECTRONIQUEMENT" stamp with the signing date.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, FileText, PenNib, CheckCircle, Buildings, ArrowClockwise,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SignPayslip() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('choose');  // choose | canvas | accept | done
  const [busy, setBusy] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const loadMeta = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/api/public/payslip/${token}`);
      setMeta(data);
      if (data.signed) setMode('done');
    } catch (e) {
      setError(e.response?.status === 404
        ? 'Bulletin de paie introuvable ou lien invalide.'
        : 'Erreur de chargement du bulletin.');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadMeta(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------- Canvas helpers --------
  const initCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = '#0F172A';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => { if (mode === 'canvas') { initCanvas(); setHasDrawn(false); } }, [mode]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * (c.width / rect.width), y: y * (c.height / rect.height) };
  };
  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const end = () => { drawing.current = false; };

  const clearCanvas = () => { initCanvas(); setHasDrawn(false); };

  const submitCanvas = async () => {
    if (!hasDrawn) { toast.error('Veuillez dessiner votre signature'); return; }
    setBusy(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      await axios.post(`${API}/api/public/payslip/${token}/sign`, {
        type: 'canvas',
        data: dataUrl,
      });
      toast.success('Signature enregistrée. Merci !');
      loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec de la signature');
    } finally { setBusy(false); }
  };

  const submitAccept = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/public/payslip/${token}/sign`, { type: 'accept' });
      toast.success('Bulletin signé électroniquement.');
      loadMeta();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec');
    } finally { setBusy(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }); } catch { return iso; }
  };

  const fmtPeriod = (start, end) => {
    try {
      const s = new Date(start).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      const e = new Date(end).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      return `du ${s} au ${e}`;
    } catch { return `${start} → ${end}`; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0052FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-white to-[#EFF6FF] py-6 px-4" data-testid="sign-payslip-page">
      <div className="max-w-2xl mx-auto">
        <Toaster position="top-center" richColors />
        <div className="flex items-center gap-2 mb-4 text-[#0052FF] px-1">
          <Buildings size={20} weight="duotone" />
          <span className="text-xs uppercase tracking-wide font-bold">{meta?.school_name || 'École'}</span>
        </div>

        {error ? (
          <div className="bg-white border border-red-200 rounded-2xl p-8 text-center shadow-sm">
            <div className="inline-flex w-14 h-14 rounded-full bg-red-100 text-red-600 items-center justify-center mb-3">
              <FileText size={28} weight="duotone" />
            </div>
            <h1 className="font-outfit text-xl font-bold text-red-700 mb-1">Lien invalide</h1>
            <p className="text-sm text-[#64748B]">{error}</p>
          </div>
        ) : meta ? (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm overflow-hidden">
            {/* --------- Header --------- */}
            <div className="bg-gradient-to-r from-[#0052FF] to-[#00A3FF] text-white px-6 py-5">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-wide opacity-90 font-bold">Bulletin de paie</p>
                  <h1 className="text-2xl font-outfit font-bold" data-testid="sign-employee-name">
                    {meta.employee_name}
                  </h1>
                  <p className="text-xs opacity-90">
                    {meta.function || '—'} · {fmtPeriod(meta.period_start, meta.period_end)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-wide opacity-90">Net à payer</p>
                  <p className="text-2xl font-outfit font-bold" data-testid="sign-total-pay">
                    {(meta.total_pay || 0).toLocaleString('fr-FR')} F
                  </p>
                </div>
              </div>
            </div>

            {/* --------- Summary --------- */}
            <div className="p-6 space-y-4">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-[#94A3B8] text-xs">Heures régulières</dt>
                  <dd className="font-mono font-semibold">{meta.hours_regular ?? 0} h</dd>
                </div>
                <div>
                  <dt className="text-[#94A3B8] text-xs">Heures supplémentaires</dt>
                  <dd className="font-mono font-semibold text-emerald-700">
                    {(meta.hours_overtime ?? 0) > 0 ? `+${meta.hours_overtime}` : '—'} h
                  </dd>
                </div>
                <div>
                  <dt className="text-[#94A3B8] text-xs">Heures attendues</dt>
                  <dd className="font-mono">{meta.expected_hours ?? 0} h</dd>
                </div>
                <div>
                  <dt className="text-[#94A3B8] text-xs">Statut</dt>
                  <dd>
                    {meta.signed ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold">
                        <CheckCircle size={16} weight="fill" /> Signé
                      </span>
                    ) : (
                      <span className="text-amber-700 font-semibold">En attente de signature</span>
                    )}
                  </dd>
                </div>
              </dl>

              <a
                href={`${API}/api/public/payslip/${token}/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0052FF] hover:underline"
                data-testid="sign-open-pdf-link"
              >
                <FileText size={16} weight="duotone" /> Consulter le bulletin PDF
              </a>

              {/* --------- Already signed --------- */}
              {mode === 'done' || meta.signed ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="sign-done-block">
                  <div className="inline-flex w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 items-center justify-center mb-2">
                    <ShieldCheck size={32} weight="fill" />
                  </div>
                  <h2 className="font-outfit text-lg font-bold text-emerald-800 mb-1">
                    Bulletin signé électroniquement
                  </h2>
                  <p className="text-sm text-emerald-800/80">
                    Signé le {fmtDate(meta.signed_at)}.
                  </p>
                  <a
                    href={`${API}/api/public/payslip/${token}/pdf`}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:underline"
                    data-testid="sign-download-signed"
                  >
                    <FileText size={16} weight="duotone" /> Télécharger le bulletin signé
                  </a>
                </div>
              ) : mode === 'choose' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setMode('canvas')}
                    className="p-5 rounded-xl border-2 border-[#0052FF] bg-blue-50 text-left hover:bg-blue-100 transition"
                    data-testid="sign-choose-canvas-btn"
                  >
                    <PenNib size={26} weight="duotone" className="text-[#0052FF] mb-1" />
                    <p className="font-outfit font-bold text-sm">Signature manuscrite</p>
                    <p className="text-xs text-[#64748B]">Dessinez votre signature au doigt ou à la souris.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('accept')}
                    className="p-5 rounded-xl border-2 border-emerald-500 bg-emerald-50 text-left hover:bg-emerald-100 transition"
                    data-testid="sign-choose-accept-btn"
                  >
                    <CheckCircle size={26} weight="duotone" className="text-emerald-600 mb-1" />
                    <p className="font-outfit font-bold text-sm">J&apos;accepte et signe</p>
                    <p className="text-xs text-[#64748B]">Signature électronique horodatée simple.</p>
                  </button>
                </div>
              ) : mode === 'canvas' ? (
                <div className="pt-2 space-y-3" data-testid="sign-canvas-block">
                  <div>
                    <p className="text-sm font-semibold mb-2">Dessinez votre signature dans le cadre ci-dessous</p>
                    <div className="border-2 border-dashed border-[#0052FF] rounded-xl bg-white overflow-hidden touch-none">
                      <canvas
                        ref={canvasRef}
                        width={600}
                        height={200}
                        className="w-full h-40 cursor-crosshair block"
                        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                        data-testid="sign-canvas"
                      />
                    </div>
                    <p className="text-[10px] text-[#94A3B8] mt-1">
                      Votre signature sera apposée sur le bulletin de paie officiel.
                    </p>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setMode('choose')} data-testid="sign-canvas-back-btn">
                      Retour
                    </Button>
                    <Button variant="outline" onClick={clearCanvas} data-testid="sign-canvas-clear-btn">
                      <ArrowClockwise size={14} className="mr-1" /> Effacer
                    </Button>
                    <Button
                      onClick={submitCanvas}
                      disabled={busy || !hasDrawn}
                      className="bg-[#0052FF] hover:bg-[#0044D4]"
                      data-testid="sign-canvas-submit-btn"
                    >
                      <PenNib size={14} className="mr-1" />
                      {busy ? 'Enregistrement…' : 'Confirmer la signature'}
                    </Button>
                  </div>
                </div>
              ) : mode === 'accept' ? (
                <div className="pt-2 space-y-3" data-testid="sign-accept-block">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <p className="text-sm text-emerald-900">
                      En cliquant sur <b>J&apos;accepte et signe</b>, vous reconnaissez avoir pris
                      connaissance des éléments de rémunération ci-dessus et vous les acceptez.
                      Un horodatage électronique sera apposé sur le bulletin final.
                    </p>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setMode('choose')} data-testid="sign-accept-back-btn">
                      Retour
                    </Button>
                    <Button
                      onClick={submitAccept}
                      disabled={busy}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="sign-accept-submit-btn"
                    >
                      <CheckCircle size={14} className="mr-1" weight="fill" />
                      {busy ? 'Enregistrement…' : "J'accepte et signe"}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="border-t border-[#E2E8F0] px-6 py-3 text-[10px] text-[#94A3B8] text-center">
              Lien de signature sécurisé — bulletin protégé par un jeton unique.
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
